import React from 'react';
import { Link } from 'react-router-dom';
import SEO from "../components/SEO";

// Example e-commerce categories
const categories = [
    { key: 'electronics-gadgets', name: 'Electronics & Gadgets', description: 'Discover the latest electronics, gadgets, and accessories for your digital lifestyle. Offerings include smartphones, wearables, audio devices, and smart home gadgets.' },
    { key: 'fashion-apparel', name: 'Fashion & Apparel', description: 'Explore trendy clothing, shoes, and accessories for men, women, and kids. Seasonal collections, essentials, and curated designer picks.' },
    { key: 'home-kitchen', name: 'Home & Kitchen', description: 'Upgrade your living space with quality home appliances, decor, and kitchen essentials. From cookware to smart appliances, find items to make life easier.' },
    { key: 'food-court', name: 'Food Court', description: 'Get your restaurant online with ordering, local delivery, and multi-branch management. Supports menus, promotions, and POS integrations.' },
    { key: 'beauty-cosmetic', name: 'Beauty & Cosmetic', description: 'Scale your beauty store with modular storefronts, inventory management, and hyperlocal delivery integrations.' },
];



const ECommerce = () => (
   <>
      <SEO
        title="E-Commerce Development & Solutions"
        description="Build a scalable, secure and conversion-focused e-commerce store with 100 Eyes Technologies. We deliver custom e-commerce development, payment integration, marketplace solutions, and growth strategies for startups and SMEs."
        keywords="E-commerce development India, online store development, Shopify alternatives, custom e-commerce, payment gateway integration, marketplace development, 100 Eyes Technologies"
      />
     <div>
        <h1>E-Commerce Categories</h1>
        <p>
            Welcome to our e-commerce platform, your one-stop destination for a wide range of products. Browse through our curated categories to find the best deals and latest trends in electronics, fashion, and home essentials. Enjoy a seamless shopping experience with secure checkout and fast delivery.
        </p>
        <div className="row g-4">
            {categories.map((cat) => (
                <div className="col-md-6" key={cat.key}>
                    <div className="card h-100">
                        <div className="card-body">
                            <h3><Link to={`/category/${cat.key}`}>{cat.name}</Link></h3>
                            <p>{cat.description}</p>
                            <Link to={`/category/${cat.key}`} className="btn btn-primary">Explore {cat.name}</Link>
                        </div>
                    </div>
                </div>
            ))}
        </div>
    </div>
    <main className="container py-5">
        <h1 className="mb-4 text-center">E-Commerce Development & Solutions</h1>

        <section className="mb-5">
          <p>
            At <strong>100 Eyes Technologies</strong>, we build modern, secure, and conversion-optimised <strong>e-commerce platforms</strong> that help startups and SMEs sell online, scale revenue, and delight customers. Whether you’re launching your first online store, migrating from a legacy platform, or building a multi-vendor marketplace, our team delivers end-to-end e-commerce solutions tailored to your business goals.
          </p>
          <p>
            We combine UX-first design, robust backend architecture, reliable payment flows, and marketing-ready features to create online stores that not only look great but also perform under real-world traffic, convert visitors into buyers, and simplify operations for store owners.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Why Invest in a Professional E-Commerce Solution?</h2>
          <p>
            The e-commerce landscape is crowded and competitive. A templated or poorly-implemented store often causes slow performance, checkout abandonment, and security risks — costing you customers and trust. A professionally built e-commerce platform ensures:
          </p>
          <ul className="list-group list-group-flush mb-3">
            <li className="list-group-item">Fast page loads and mobile-optimised experience</li>
            <li className="list-group-item">Secure transactions and PCI-compliant payment flows</li>
            <li className="list-group-item">SEO-friendly product pages and structured data</li>
            <li className="list-group-item">Streamlined inventory, order and return management</li>
            <li className="list-group-item">Integration with shipping, accounting and marketing tools</li>
          </ul>
          <p>
            Investing in the right e-commerce foundation reduces friction at every stage of the customer journey, increases conversion rates, and makes scaling simpler and cheaper in the long run.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Our E-Commerce Development Services</h2>
          <p>
            We offer a full-suite of services to cover the entire lifecycle of your online business:
          </p>
          <ul className="list-group list-group-flush">
            <li className="list-group-item">
              <strong>Custom Online Store Development:</strong> Tailor-made storefronts built with React, Next.js or your preferred frontend, coupled with Node.js, PHP or Python backends — designed for speed and conversion.
            </li>
            <li className="list-group-item">
              <strong>Platform Migrations:</strong> Hassle-free migrations from legacy systems or hosted platforms (WooCommerce, Magento, Wix, older custom builds) to modern, maintainable solutions with zero data loss.
            </li>
            <li className="list-group-item">
              <strong>Headless Commerce:</strong> Decouple frontend and backend for faster experiences and multi-channel delivery — ideal for brands that want flexibility and performance.
            </li>
            <li className="list-group-item">
              <strong>Multi-vendor Marketplace Development:</strong> Build marketplaces with vendor onboarding, commission management, seller dashboards, and quality controls.
            </li>
            <li className="list-group-item">
              <strong>Payment Gateway & Wallet Integration:</strong> Secure integration with Razorpay, PayU, Stripe, Paytm, UPI and international gateways; support for wallets and EMI options.
            </li>
            <li className="list-group-item">
              <strong>Shipping & Logistics Integration:</strong> Real-time shipping rates, label printing, AWB tracking and returns management integrated with major carriers and third-party logistics providers.
            </li>
            <li className="list-group-item">
              <strong>Subscription & Recurring Billing:</strong> Recurring payments, subscription management, trials and dunning logic for subscription-based businesses.
            </li>
            <li className="list-group-item">
              <strong>Performance & Security:</strong> CDN, caching, SSL, WAF recommendations and PCI compliance to keep the store fast and secure.
            </li>
            <li className="list-group-item">
              <strong>Analytics & Growth Tools:</strong> Integration with Google Analytics 4, server-side tracking, CRO experiments, and marketing pixels for data-driven growth.
            </li>
          </ul>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Our E-Commerce Development Process</h2>
          <p>
            We follow a pragmatic process that reduces risk and accelerates time-to-market:
          </p>
          <ol className="list-group list-group-numbered mb-3">
            <li className="list-group-item">
              <strong>Discovery & Strategy:</strong> Identify product lines, customer personas, purchase flows and KPIs. Define payment, shipping and tax rules based on target markets.
            </li>
            <li className="list-group-item">
              <strong>UX & CRO-focused Design:</strong> Wireframes and high-fidelity prototypes focused on conversion — product pages, listing filters, and checkout should remove doubts and speed decisions.
            </li>
            <li className="list-group-item">
              <strong>Architecture & Tech Stack:</strong> Decide between monolith vs headless, database choices, caching strategy and integration points for ERP/CRM/warehouses.
            </li>
            <li className="list-group-item">
              <strong>Implementation & Iteration:</strong> Agile sprints with frequent demos. We prioritize a Minimum Viable Store (MVS) to launch faster and iterate based on user data.
            </li>
            <li className="list-group-item">
              <strong>Testing & QA:</strong> Performance testing, security audits, payment flow validation, and cross-device compatibility checks.
            </li>
            <li className="list-group-item">
              <strong>Launch & Post-Launch Support:</strong> Smooth rollout, monitoring, and continuous optimisation — including conversion rate optimisation (CRO) and A/B experiments.
            </li>
          </ol>
          <p>
            This approach ensures a stable launch while unlocking continuous improvement based on real customer behaviour.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Key Pages & Features We Build</h2>
          <p>
            A great store is more than a product listing. We design the entire buyer journey:
          </p>
          <ul className="list-group list-group-flush">
            <li className="list-group-item">SEO-optimized product pages with rich snippets and structured data</li>
            <li className="list-group-item">Category pages with layered filters and sorting</li>
            <li className="list-group-item">Optimized checkout (one-page/guest checkout/express options)</li>
            <li className="list-group-item">Customer accounts, order history and returns portal</li>
            <li className="list-group-item">Dynamic promotions, coupon engine and upsell flows</li>
            <li className="list-group-item">Wishlist, reviews, ratings and social proof components</li>
            <li className="list-group-item">Admin panels for inventory, orders, customers and analytics</li>
          </ul>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Mobile Commerce & Progressive Web Apps (PWAs)</h2>
          <p>
            Mobile-first shoppers expect speed and simplicity. We build mobile-optimized experiences and PWAs that behave like native apps — fast loads, home-screen install prompts, offline caching and push notifications. PWAs are a cost-effective way to reach mobile customers with app-like performance without the overhead of app store approvals.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Marketplace — Multi-Vendor Solutions</h2>
          <p>
            If your goal is a marketplace, we design with seller experience and trust in mind: seller onboarding flows, KYC checks, product moderation, seller dashboards, commission rules, dispute management and escrow flows where necessary. Marketplaces have different success factors than single-vendor stores — we help you define platform governance and growth loops to attract buyers and sellers simultaneously.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Integrations That Simplify Operations</h2>
          <p>
            A modern e-commerce stack integrates deeply with your other systems. We connect your store to:
          </p>
          <ul className="list-group list-group-flush">
            <li className="list-group-item">ERPs and accounting software (Tally, QuickBooks, Zoho Books)</li>
            <li className="list-group-item">Shipping aggregators and logistic partners</li>
            <li className="list-group-item">Payment gateways, wallets and UPI solutions</li>
            <li className="list-group-item">Marketplaces like Amazon/Flipkart for syndication</li>
            <li className="list-group-item">CRM & email marketing tools for lifecycle campaigns</li>
            <li className="list-group-item">Analytics platforms and BI tools</li>
          </ul>
          <p>
            These integrations reduce manual work, improve order accuracy and help you scale operations with fewer errors.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Security, Compliance & Payment Safety</h2>
          <p>
            Security is non-negotiable for e-commerce. We implement SSL, tokenized payments, PCI-DSS compliant flows where required, secure web application practices and regular security testing. For marketplaces and larger stores, we advise on fraud detection, chargeback handling and secure storage of sensitive data.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Conversion Rate Optimisation (CRO) & Marketing</h2>
          <p>
            Building a store is step one — growing conversions is step two. Our CRO work focuses on improving product discoverability, optimizing product pages for intent, simplifying checkout and reducing friction. We run A/B tests, heatmap analysis and funnel tracking to find and fix leaks in the purchase funnel.
          </p>
          <p>
            Additionally, we help set up digital marketing channels — SEO, paid ads, affiliate and influencer programs, email automation and retargeting — to drive early traction and sustainable growth.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Analytics & Growth Reporting</h2>
          <p>
            Data should drive decisions. We implement analytics tracking (including server-side where needed), custom dashboards, cohort analysis and LTV/CAC metrics so you can prioritize product lines, marketing channels, and retention efforts with clarity.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Why Choose 100 Eyes Technologies for E-Commerce</h2>
          <ul className="list-group list-group-flush">
            <li className="list-group-item">End-to-end e-commerce expertise — design, engineering, operations & marketing</li>
            <li className="list-group-item">Custom solutions that fit your business model and budget</li>
            <li className="list-group-item">Focus on performance, security and conversion — not just features</li>
            <li className="list-group-item">Proven process for faster launches and continuous growth</li>
            <li className="list-group-item">Post-launch support, monitoring and optimisation services</li>
          </ul>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Getting Started — Roadmap to Launch</h2>
          <p>
            Getting an e-commerce store live can be broken down into clear milestones:
          </p>
          <ol className="list-group list-group-numbered mb-3">
            <li className="list-group-item">Discovery & product-market fit assessment</li>
            <li className="list-group-item">MVS (Minimum Viable Store) build & internal testing</li>
            <li className="list-group-item">Soft-launch (beta users / limited geography)</li>
            <li className="list-group-item">Scale, integrate, and optimise based on user data</li>
          </ol>
          <p>
            We help you pick the right launch strategy based on your product type, inventory, logistics and marketing budget.
          </p>
        </section>

        <section className="mb-5">
          <h2 className="mb-3">Get In Touch</h2>
          <p>
            Ready to build or upgrade your e-commerce business? <strong>100 Eyes Technologies</strong> will help you craft an online commerce solution that drives sales, reduces operational friction, and positions you to scale. Contact us for a discovery session — we’ll map requirements, propose the right stack, and give you a clear roadmap to launch.
          </p>
          <p>
            Let's build an e-commerce experience your customers love and a commerce engine that powers sustainable business growth.
          </p>
        </section>
      </main>
    </>
);

export default ECommerce;